/**
 * Calendar View Page
 * Visual scheduling interface for posts with drag-and-drop
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../contexts/BrandContext';
import { apiClient } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import type { CalendarEvent } from '../types';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isToday, addMonths, subMonths } from 'date-fns';
import { Icon } from '../components/icons';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';

// Draggable Event Component
function DraggableEvent({ event, onNavigate }: { event: CalendarEvent; onNavigate: () => void }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: event.id,
    data: event,
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        opacity: isDragging ? 0.5 : 1,
      }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded cursor-move hover:bg-purple-200 transition-colors flex items-center gap-1 ${
        isDragging ? 'opacity-50' : ''
      }`}
      {...listeners}
      {...attributes}
    >
      <Icon name="menu" size="xs" className="flex-shrink-0" />
      <span className="truncate" onClick={(e) => { e.stopPropagation(); onNavigate(); }}>
        {format(new Date(event.start), 'h:mm a')} - {event.title}
      </span>
    </div>
  );
}

// Droppable Day Component
function DroppableDay({ dayKey, day, events, today, onNavigate }: {
  dayKey: string;
  day: Date;
  events: CalendarEvent[];
  today: boolean;
  onNavigate: (id: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: dayKey,
    data: { date: day },
  });

  return (
    <div
      ref={setNodeRef}
      className={`bg-white min-h-[120px] p-2 transition-colors ${
        today ? 'ring-2 ring-blue-500' : ''
      } ${isOver ? 'bg-purple-50 ring-2 ring-purple-300' : ''}`}
    >
      <div
        className={`text-sm font-medium mb-2 ${
          today ? 'text-blue-600' : 'text-gray-900'
        }`}
      >
        {format(day, 'd')}
      </div>
      <div className="space-y-1">
        {events.map((event) => (
          <DraggableEvent
            key={event.id}
            event={event}
            onNavigate={() => onNavigate(event.id)}
          />
        ))}
      </div>
    </div>
  );
}

export function CalendarView() {
  const { selectedBrand } = useBrand();
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [activeEvent, setActiveEvent] = useState<CalendarEvent | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    if (selectedBrand) {
      fetchEvents();
    }
  }, [selectedBrand, currentMonth]);

  const fetchEvents = async () => {
    if (!selectedBrand) return;

    try {
      const start = startOfMonth(currentMonth).getTime();
      const end = endOfMonth(currentMonth).getTime();

      const response = await apiClient.getCalendarEvents({
        start,
        end,
        brand_id: selectedBrand.id,
      });

      if (response.success && response.data) {
        setEvents(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch calendar events:', error);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const draggedEvent = events.find(e => e.id === event.active.id);
    if (draggedEvent) {
      setActiveEvent(draggedEvent);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      setActiveEvent(null);
      return;
    }

    const draggedEvent = events.find(e => e.id === active.id);
    const targetDayData = over.data.current as { date: Date } | undefined;

    if (draggedEvent && targetDayData) {
      try {
        // Preserve the original time but change the date
        const originalDate = new Date(draggedEvent.start);
        const newDate = new Date(targetDayData.date);
        newDate.setHours(originalDate.getHours());
        newDate.setMinutes(originalDate.getMinutes());
        newDate.setSeconds(0);
        newDate.setMilliseconds(0);

        const newTimestamp = newDate.getTime();

        // Update the post's scheduled_at via API
        await apiClient.updatePost(draggedEvent.id, {
          scheduled_at: newTimestamp,
        });

        // Optimistically update local state
        setEvents(prevEvents =>
          prevEvents.map(e =>
            e.id === draggedEvent.id
              ? { ...e, start: newTimestamp, end: newTimestamp }
              : e
          )
        );
      } catch (error) {
        console.error('Failed to reschedule post:', error);
        // Could show an error toast here
        // Refetch to revert optimistic update
        fetchEvents();
      }
    }

    setActiveEvent(null);
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Group events by day
  const eventsByDay: Record<string, CalendarEvent[]> = {};
  events.forEach((event) => {
    const dayKey = format(new Date(event.start), 'yyyy-MM-dd');
    if (!eventsByDay[dayKey]) {
      eventsByDay[dayKey] = [];
    }
    eventsByDay[dayKey].push(event);
  });

  if (!selectedBrand) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="text-center">
          <p className="text-gray-500">Please select a brand from the dashboard</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <Card className="text-center py-12">
        <Icon name="calendar" size="3xl" className="mx-auto mb-4 text-gray-400 animate-pulse" />
        <p className="text-gray-500">No scheduled posts this month</p>
        <Button className="mt-4" onClick={() => navigate('/posts/new')}>
          Create & schedule a post
        </Button>
      </Card>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Calendar</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">
              Schedule posts for{' '}
              <span
                className="font-medium"
                style={{ color: selectedBrand.color_primary }}
              >
                {selectedBrand.name}
              </span>
              <span className="hidden sm:inline">{' • Drag & drop to reschedule'}</span>
            </p>
          </div>
          <Button onClick={() => navigate('/posts/new')} className="w-full sm:w-auto">
            Schedule Post
          </Button>
        </div>

        {/* Calendar Navigation */}
        <Card>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              >
                <Icon name="arrow-left" size="sm" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentMonth(new Date())}
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              >
                <Icon name="arrow-right" size="sm" />
              </Button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="min-w-[640px] grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden">
            {/* Weekday Headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div
                key={day}
                className="bg-gray-50 px-2 py-3 text-center text-sm font-medium text-gray-700"
              >
                {day}
              </div>
            ))}

            {/* Calendar Days */}
            {daysInMonth.map((day) => {
              const dayKey = format(day, 'yyyy-MM-dd');
              const dayEvents = eventsByDay[dayKey] || [];
              const today = isToday(day);

              return (
                <DroppableDay
                  key={day.toISOString()}
                  dayKey={dayKey}
                  day={day}
                  events={dayEvents}
                  today={today}
                  onNavigate={(id) => navigate(`/posts/${id}`)}
                />
              );
            })}
            </div>
          </div>
        </Card>

      {/* Upcoming Events */}
      {events.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Scheduled Posts ({events.length})
          </h3>
          <div className="space-y-3">
            {events.slice(0, 5).map((event) => (
              <div
                key={event.id}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => navigate(`/posts/${event.id}`)}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded">
                    <Icon name="calendar" size="md" className="text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{event.title}</p>
                    <p className="text-sm text-gray-500">
                      {format(new Date(event.start), 'MMM d, yyyy h:mm a')} •{' '}
                      {event.platforms.join(', ')}
                    </p>
                    {event.results && event.results.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {event.results.map((result) => (
                          <span
                            key={result.id}
                            className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${
                              result.status === 'success'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {result.platform}
                          </span>
                        ))}
                      </div>
                    )}
                    {event.status === 'failed' && event.error_message && (
                      <p className="text-xs text-red-600 mt-1 line-clamp-2">
                        {event.error_message}
                      </p>
                    )}
                  </div>
                </div>
                <span
                  className={`text-sm font-medium ${
                    event.status === 'published'
                      ? 'text-green-600'
                      : event.status === 'failed'
                      ? 'text-red-600'
                      : 'text-purple-600'
                  }`}
                >
                  {event.status}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {activeEvent ? (
          <div className="text-xs px-2 py-1 bg-purple-200 text-purple-900 rounded shadow-lg flex items-center gap-1">
            <Icon name="menu" size="xs" />
            <span>
              {format(new Date(activeEvent.start), 'h:mm a')} - {activeEvent.title}
            </span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
